/**
 * The words an exit answers to — D227 (E21, Calder's `the-road-climbs` §1).
 *
 * @remarks
 * An exit has one `directionToken`, which is the word the MAP draws it by,
 * and may answer to others (`alsoAnswersTo`): a road that climbs north
 * answers `up`, a doorway that is also north answers `in`. Movement reads
 * every word; the map reads the first alone, so one exit is one edge however
 * many words reach it. Both readers live here so neither can drift from the
 * other: `wordsAnExitAnswersTo` is the set, `exitAnsweringTo` the lookup.
 *
 * @packageDocumentation
 */

import type { ExitDefinition } from '@orpheus/content-schema';

/**
 * Every direction word this exit answers to, its own `directionToken` first.
 *
 * @param exit - The exit
 * @returns The words, in authored order
 */
export const wordsAnExitAnswersTo = (exit: ExitDefinition): readonly string[] => [exit.directionToken, ...(exit.alsoAnswersTo ?? [])];

/**
 * The exit among these that answers to a direction word, if one does.
 *
 * @param exits - The exits to search (the room's visible ones, usually)
 * @param directionWord - The canonical direction the player moved by
 * @returns The exit, or `undefined` when nothing leads that way
 */
export const exitAnsweringTo = (exits: readonly ExitDefinition[], directionWord: string): ExitDefinition | undefined =>
	exits.find((exit) => wordsAnExitAnswersTo(exit).includes(directionWord));
